import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { BillingStatus } from "../api/billing.api";

interface UsageCardProps {
  usage: NonNullable<BillingStatus["usage"]>;
  children?: ReactNode;
}

/**
 * Displays Monthly Active Devices (MAD) usage with
 * progress bar, percentage, and overage estimate.
 */
export function UsageCard({ usage, children }: UsageCardProps) {
  const { t } = useTranslation();

  const progressColor =
    usage.mad_quota_used_pct < 80
      ? "from-success to-emerald-400"
      : usage.mad_quota_used_pct < 100
        ? "from-warning to-amber-400"
        : "from-danger to-rose-400";

  return (
    <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-heading">{t("billing.usageTitle")}</h3>
          <span className="text-xs text-muted font-medium bg-elevated px-2.5 py-1 rounded-md border border-border/40">
            {t("billing.currentPeriod", { period: usage.period })}
          </span>
        </div>

        {/* Progress Bar Container */}
        <div>
          <div className="flex justify-between items-baseline mb-2">
            <span className="text-sm font-medium text-body">{t("billing.madUsage")}</span>
            <span className="text-sm font-bold text-heading">
              {usage.current_mad.toLocaleString()} / {usage.mad_quota.toLocaleString()} MAD
            </span>
          </div>

          {/* Progress Track */}
          <div className="h-3 w-full bg-void rounded-full overflow-hidden border border-border/20 relative">
            <div
              className={`h-full rounded-full transition-all duration-500 ease-out bg-gradient-to-r ${progressColor}`}
              style={{ width: `${Math.min(usage.mad_quota_used_pct, 100)}%` }}
            />
          </div>

          <div className="mt-1.5 flex justify-between items-center text-xs text-muted">
            <span>{t("billing.quotaUsedPct", { pct: usage.mad_quota_used_pct })}</span>
            {usage.overage_estimate_cents > 0 && (
              <span className="text-xs font-semibold text-danger bg-danger/10 px-2 py-0.5 rounded-md animate-pulse">
                {t("billing.overageEstimate", {
                  amount: (usage.overage_estimate_cents / 100).toFixed(2),
                })}
              </span>
            )}
          </div>
        </div>
        {children}

        {/* Footer */}
        <div className="border-t border-border/40 pt-4 flex items-center gap-2 text-xs text-muted">
          <svg
            className="h-4 w-4 shrink-0 text-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{t("billing.retentionDays", { days: usage.retention_days })}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Placeholder shown when usage data is not yet available.
 */
export function UsagePlaceholder() {
  const { t } = useTranslation();

  return (
    <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
      <h3 className="text-base font-semibold text-heading">{t("billing.usageTitle")}</h3>
      <p className="mt-3 text-sm text-muted">{t("billing.usagePlaceholder")}</p>
    </div>
  );
}
